import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#D1FAE5]">
      {/* Header */}
      <div className="bg-[#064E3B] text-white py-6">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold">Terms & Conditions</h1>
          <p className="text-gray-300 mt-2">Last updated: June 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
          
          <div className="flex items-center gap-3 mb-8">
            <FileText className="h-8 w-8 text-[#064E3B]" />
            <h2 className="text-2xl font-bold text-[#064E3B]">Terms of Service</h2>
          </div>

          <div className="space-y-8 text-gray-700">
            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">1. Acceptance of Terms</h3>
              <p>
                By accessing or using MoneyFeed services, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">2. Description of Service</h3>
              <p className="mb-3">
                MoneyFeed provides AI-powered bookkeeping and accounting tools, available through WhatsApp, our web app, and our iOS and Android apps. Our services include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Natural language expense and income tracking</li>
                <li>Receipt capture and scanning (including your device camera)</li>
                <li>Mileage tracking and tax deduction estimates</li>
                <li>Bill management, reminders and push notifications</li>
                <li>Client invoicing</li>
                <li>Financial reports, tax summaries and insights</li>
                <li>Multi-currency support</li>
                <li>Multi-account workspaces with team roles</li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">3. User Responsibilities</h3>
              </div>
              <p className="mb-3">As a user of our services, you agree to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account</li>
                <li>Use the service only for lawful purposes</li>
                <li>Not attempt to access other users' data</li>
                <li>Not use automated systems to access our services without permission</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">4. Plans, Subscriptions & Billing</h3>
              <p className="mb-3">
                MoneyFeed offers a free tier and paid subscription tiers (currently <strong>Plus</strong>, <strong>Pro</strong> and <strong>Business</strong>) that unlock additional features such as higher limits, advanced reports, invoicing and team access. The exact plans, features and prices for your region and platform are shown in the app <strong>at the point of purchase</strong>, and may differ between our web app and the mobile app stores.
              </p>
              <p className="mb-3">
                Paid plans are offered as <strong>auto-renewing subscriptions</strong>. Your subscription automatically renews at the end of each billing period unless you cancel at least 24 hours before the period ends. You can cancel at any time; access continues until the end of the current paid period.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Apple App Store (iOS):</strong> purchases and renewals are billed to your Apple Account. Payment is charged at confirmation of purchase, and your account is charged for renewal within 24 hours before the period ends. Manage or cancel your subscription in your Apple Account settings. Refunds for App Store purchases are handled by Apple under Apple's policies.</li>
                <li><strong>Web &amp; Google Play (Android):</strong> purchases are processed by our payment provider (Stripe). You can manage or cancel from Settings in the web app.</li>
                <li>Prices may change; we will give notice of material changes, and changes take effect on your next renewal.</li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <h3 className="text-xl font-semibold text-[#064E3B]">5. Limitations of Liability</h3>
              </div>
              <p className="mb-3">
                MoneyFeed is a financial tracking tool and does not provide financial advice. We are not liable for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Financial decisions made based on our reports</li>
                <li>Errors in AI-parsed transactions (always verify important entries)</li>
                <li>Service interruptions or data loss beyond our control</li>
                <li>Third-party service issues (WhatsApp, payment processors)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">6. Termination</h3>
              <p>
                We reserve the right to suspend or terminate your account if you violate these terms. You may also delete your account at any time through the Settings page. Upon termination, your data will be deleted according to our data retention policy.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">7. Changes to Terms</h3>
              <p>
                We may update these terms from time to time. We will notify you of significant changes via WhatsApp or email. Continued use of our services after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">8. WhatsApp Communications & Consent</h3>
              </div>
              <p className="mb-3">
                MoneyFeed is delivered primarily through WhatsApp. By registering with your phone number and accepting these Terms, <strong>you expressly consent to receive messages from MoneyFeed on WhatsApp</strong>, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-3">
                <li>Service messages (transaction confirmations, bill reminders, reports, account and security notifications)</li>
                <li>Onboarding and welcome messages</li>
                <li>Product updates and promotional messages about features and plans</li>
              </ul>
              <p className="mb-3">
                Message frequency varies based on your activity. Message and data rates may apply. Your consent is not a condition of purchase.
              </p>
              <p>
                <strong>You can opt out at any time</strong> by replying <strong>STOP</strong> to any of our WhatsApp messages (or by contacting us). You will stop receiving reminders and promotional messages, and can re-subscribe by replying <strong>START</strong>. Opting out does not delete your account or data — you can still message us to track transactions.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">9. Contact</h3>
              <p>
                For questions about these Terms & Conditions, please contact us:
              </p>
              <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                <p><strong>Email:</strong> feed@maxefeed.com</p>
                <p><strong>Phone:</strong> +1 (832) 688-6863</p>
                <p><strong>Address:</strong> 5900 Balcones Drive 7274, Austin, TX 78731</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
